import React from "react";
import { useTranslation } from "react-i18next";
import { AssetStatsView } from "../../../../api/General/assets";
import { Card, CardContent } from "@/components/ui/Card";

interface AssetStatsCardsProps {
  stats: AssetStatsView | null;
  loading: boolean;
}

const AssetStatsCards: React.FC<AssetStatsCardsProps> = ({ stats, loading }) => {
  const { t } = useTranslation();

  const awaitingCount = stats?.awaitingFulfillment ?? 0;

  const cards = [
    { label: t("assets.totalAssets"), value: stats?.totalAssets ?? 0, color: "bg-blue-50 text-blue-700 border-blue-200" },
    { label: t("assets.available"), value: stats?.available ?? 0, color: "bg-green-50 text-green-700 border-green-200" },
    { label: t("assets.issued"), value: stats?.issued ?? 0, color: "bg-indigo-50 text-indigo-700 border-indigo-200" },
    { label: t("assets.underMaintenance"), value: stats?.underMaintenance ?? 0, color: "bg-yellow-50 text-yellow-700 border-yellow-200" },
    { label: t("assets.totalValue"), value: stats?.totalValue ?? 0, isCurrency: true, color: "bg-purple-50 text-purple-700 border-purple-200" },
  ];

  if (loading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {cards.map((_, i) => (
          <Card key={i} variant="elevated">
            <CardContent className="p-4 animate-pulse">
              <div className="h-4 bg-muted rounded w-2/3 mb-2" />
              <div className="h-8 bg-muted rounded w-1/2" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {cards.map((card, i) => (
          <Card
            key={i}
            className={card.color}
            data-testid={`asset-stat-${i}`}
          >
            <CardContent className="p-4">
              <p className="text-sm font-medium opacity-80">{card.label}</p>
              <p className="text-2xl font-bold mt-1">
                {card.isCurrency
                  ? new Intl.NumberFormat(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(card.value)
                  : card.value}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>
      {awaitingCount > 0 && (
        <div className="flex items-center gap-2 px-3 py-2 bg-purple-50 border border-purple-200 rounded-lg" data-testid="awaiting-fulfillment-badge">
          <svg className="w-4 h-4 text-purple-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="text-sm font-medium text-purple-700">
            {t("assetRequests.awaitingFulfillment", { count: awaitingCount })}
          </span>
        </div>
      )}
    </div>
  );
};

export default AssetStatsCards;
